import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import { clearStringValue } from 'Core/data/Helpers';
import { LogManagerConsole } from 'Core/logManager';

// Main RTK Entity Adapter Configuration
export const unitAdapter = createEntityAdapter({
    selectId: (unit) => String(unit.id) // Ensure ID is string for proper indexing (as seen in CatalogProcessor map)
});

const unitSlice = createSlice({
    name: 'realtime_unit',
    initialState: unitAdapter.getInitialState(),
    reducers: {
        /**
         * Applies business rules to incoming Unit updates.
         * NOTE: The old processor merged `tags` directly here by accessing `itemsExists.tags`.
         * In a sliced RTK architecture, reducers only see their own state.
         * The hydration of `tags` should either be done in a Thunk/Listener before dispatching,
         * or more gracefully, accessed via a combined Selector `createSelector([selectUnit, selectTags], ...)`
         */
        upsertManyWithRules: (state, action) => {
            const items = action.payload;

            LogManagerConsole("RT_DEBUG", "[UnitSlice] upsertManyWithRules dispatched incoming size:", items ? items.length : 0);

            if (!items || !items.length) return;

            const validUpdates = [];

            for (const newItem of items) {
                // Ensure ID extraction works for all payloads
                const unitId = Number(newItem.id || newItem.CUnits_id);
                if (!unitId || unitId === 'undefined') continue;

                const existingItem = state.entities[unitId];
                if (!existingItem) {
                    LogManagerConsole("Unit-Rejected", "**** Rejected Socket Unit: (Unit not found)", { unitId, newItemRaw: newItem });
                    continue;
                }

                // Handle basic asset merge to replace the old objAsset dependency 
                let mergedAsset = existingItem.asset;
                if (newItem.asset) {
                    mergedAsset = { ...(existingItem.asset || {}), ...newItem.asset };
                }

                // If tags are explicitly passed in the payload, store them; otherwise retain old
                let tags = newItem.tags && Array.isArray(newItem.tags) ? newItem.tags : existingItem.tags || [];
                let tagIds = newItem.tagIds !== undefined ? newItem.tagIds : existingItem.tagIds;

                const objItem = {
                    ...existingItem,
                    id: Number(unitId),
                    label: newItem?.Label || newItem?.label || existingItem?.label,
                    esn: (newItem?.USN || newItem?.esn) || existingItem.esn,
                    imei: (newItem?.IMEI || newItem?.imei) || existingItem.imei,
                    useEcuOdometer: newItem?.useEcuOdometer !== undefined
                        ? Boolean(newItem?.useEcuOdometer) : newItem?.ecuodometer
                            ? newItem?.ecuodometer !== undefined && Boolean(newItem?.ecuodometer) : existingItem?.useEcuOdometer
                                ? existingItem?.useEcuOdometer !== undefined && Boolean(existingItem?.useEcuOdometer) : false,
                    offsetEcuEngineOdometer:
                        Object.prototype.hasOwnProperty.call(newItem, 'offsetEcuEngineOdometer')
                            ? (newItem.offsetEcuEngineOdometer == null
                                ? null
                                : Number(newItem.offsetEcuEngineOdometer).toFixed(2))
                            : (existingItem.offsetEcuEngineOdometer == null
                                ? null
                                : Number(existingItem.offsetEcuEngineOdometer).toFixed(2)),
                    offsetEngineOdometer:
                        Object.prototype.hasOwnProperty.call(newItem, 'offsetEngineOdometer')
                            ? (newItem.offsetEngineOdometer == null
                                ? null
                                : Number(newItem.offsetEngineOdometer).toFixed(2))
                            : (existingItem.offsetEngineOdometer == null
                                ? null
                                : Number(existingItem.offsetEngineOdometer).toFixed(2)),
                    useEcuEngineHours: newItem?.useEcuEngineHours || existingItem?.useEcuEngineHours || false,
                    offsetEcuEngineHours:
                        Object.prototype.hasOwnProperty.call(newItem, 'offsetEcuEngineHours')
                            ? (newItem.offsetEcuEngineHours == null
                                ? null
                                : Number(newItem.offsetEcuEngineHours).toFixed(2))
                            : (existingItem.offsetEcuEngineHours == null
                                ? null
                                : Number(existingItem.offsetEcuEngineHours).toFixed(2)),
                    offsetEngineHours:
                        Object.prototype.hasOwnProperty.call(newItem, 'offsetEngineHours')
                            ? (newItem.offsetEngineHours == null
                                ? null
                                : Number(newItem.offsetEngineHours).toFixed(2))
                            : (existingItem.offsetEngineHours == null
                                ? null
                                : Number(existingItem.offsetEngineHours).toFixed(2)),
                    useEcuVin: newItem?.useEcuVin !== undefined
                        ? Boolean(newItem?.useEcuVin) : newItem?.ecuVin
                            ? newItem?.ecuVin !== undefined && Boolean(newItem?.ecuVin) : existingItem?.useEcuVin
                                ? existingItem?.useEcuVin !== undefined && Boolean(existingItem?.useEcuVin) : false,
                    jbus: Boolean(Number(newItem?.jbus)) || Boolean(Number(existingItem?.jbus)),
                    vbus: Boolean(Number(newItem?.vbus)) || Boolean(Number(existingItem?.vbus)),
                    driverId: (newItem?.DID || newItem?.driverId) !== undefined ? (newItem?.DID || newItem?.driverId) : existingItem?.driverId,
                    driverName: newItem?.driverName !== undefined || ((newItem?.DID || newItem?.driverId) !== undefined && (newItem?.DID || newItem?.driverId) == 0) ? clearStringValue(newItem?.driverName) : existingItem?.driverName,
                    defaultDriverId: newItem?.defaultDriverId !== undefined ? newItem?.defaultDriverId : existingItem?.defaultDriverId,
                    driverExternalId: (newItem?.driverExternalId || newItem?.externalId) !== undefined ? (newItem?.driverExternalId || newItem?.externalId) : existingItem?.driverExternalId,
                    asset: mergedAsset,
                    vehicleStatusId: newItem?.vehicleStatusId == 0 || newItem?.vehiclestatus_id == 0 ? 0 : newItem?.vehiclestatus_id || newItem?.vehicleStatusId || existingItem?.vehicleStatusId,
                    hasVideo: newItem?.hasVideo !== undefined ? newItem?.hasVideo : existingItem?.hasVideo,
                    tagIds: tagIds,
                    tags: tags,
                    // Note: timezone hydration from full catalog lookup is moved to component-level or selector.
                    timeZone: newItem?.timeZone !== undefined ? newItem?.timeZone : existingItem?.timeZone,
                    starter: newItem?.starter !== undefined ? newItem?.starter : existingItem?.starter,
                    desiredStarterState: newItem?.desiredStarterState === "Enable" ? newItem?.desiredStarterState : "Disable",
                    useEcuSpeed: newItem?.useEcuSpeed || existingItem?.useEcuSpeed || false,
                    sdCard: newItem?.sdCard || existingItem?.sdCard || null,
                    maintenanceWarningEventsCount: (newItem?.maintenanceWarningEventsCount !== undefined) ? newItem?.maintenanceWarningEventsCount : existingItem?.maintenanceWarningEventsCount,
                    isOffline: newItem?.isOffline !== undefined ? newItem?.isOffline : existingItem?.isOffline !== undefined ? existingItem?.isOffline : false,
                    unitVersion: newItem?.unitVersion !== undefined ? newItem?.unitVersion : existingItem?.unitVersion,
                    odometerEnable: newItem?.unitVersion?.odometerEnable !== undefined ? newItem?.unitVersion?.odometerEnable : existingItem?.odometerEnable,
                    hoursEnable: newItem?.unitVersion?.hoursEnable !== undefined ? newItem?.unitVersion?.hoursEnable : existingItem?.hoursEnable,
                    supportedOutputs: newItem?.unitVersion?.supportedOutputs !== undefined ? newItem?.unitVersion?.supportedOutputs : existingItem?.supportedOutputs,
                    unitVersionId: newItem?.unitVersionId !== undefined ? newItem?.unitVersionId : existingItem?.unitVersionId,
                    installed: newItem?.installed !== undefined ? newItem?.installed : existingItem?.installed,
                    linkedUnit: newItem?.linkedUnit !== undefined ? newItem?.linkedUnit : existingItem?.linkedUnit,
                    typeId: newItem?.typeId !== undefined ? newItem?.typeId : existingItem?.typeId,
                    deviceCategory: newItem?.deviceCategory !== undefined ? newItem?.deviceCategory : existingItem?.deviceCategory,
                    linkedMode: newItem?.linkedMode !== undefined ? newItem?.linkedMode : existingItem?.linkedMode,
                    linkedUnitId: newItem?.linkedUnitId !== undefined ? newItem?.linkedUnitId : existingItem?.linkedUnitId,
                    manufacturerId: newItem?.manufacturerId !== undefined ? newItem?.manufacturerId : existingItem?.manufacturerId,
                    manufacturerName: newItem?.manufacturerName !== undefined ? newItem?.manufacturerName : existingItem?.manufacturerName,
                    driverBehavior: newItem?.driverBehavior !== undefined ? newItem?.driverBehavior : existingItem?.driverBehavior,
                    unitType: newItem?.unitType !== undefined ? newItem?.unitType : existingItem?.unitType,
                    icon: newItem?.icon !== undefined ? newItem?.icon : existingItem?.icon,
                    deviceModel: newItem?.deviceModel !== undefined ? newItem?.deviceModel : existingItem?.deviceModel,
                    videoDevices: newItem?.videoDevices !== undefined ? newItem?.videoDevices : existingItem?.videoDevices
                };

                validUpdates.push(objItem);
            }

            if (validUpdates.length > 0) {
                // Determine if we need to track label/icon changes for driver propagation here? 
                // A better approach is firing a Thunk that dispatches `upsertManyWithRules` for both slices.
                unitAdapter.upsertMany(state, validUpdates);
            }
        },
        // Units arriving from the initial catalog / IndexedDB cache carry only `tagIds`.
        // `tags` is hydrated later by getTagsInitialData, and only for units that own at
        // least one tag, so untagged units would keep `tags === undefined` forever and
        // break every consumer doing `unit.tags.filter(...)`. Normalize it here.
        addUnits: (state, action) => {
            const items = Array.isArray(action.payload) ? action.payload : [];
            unitAdapter.addMany(
                state,
                items.map((unit) => (Array.isArray(unit?.tags) ? unit : { ...unit, tags: [] }))
            );
        },
        removeUnits: unitAdapter.removeMany,
        clearUnits: unitAdapter.removeAll,
    }
});

export const { upsertManyWithRules, removeUnits, clearUnits, addUnits } = unitSlice.actions;

export const unitSelectors = unitAdapter.getSelectors(state => state.realtime.unit);

export default unitSlice.reducer;
